/**
 * FacetMenuTerms Component
 */

import { fuzzyFilter, sanitizeString } from '../../../../utils/string';
import './styles.scss';

import * as classNames from 'classnames';
import { isEqual } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { VelocityTransitionGroup } from 'velocity-react';

import { QueryStringContext } from '../../../../contexts/queryString';
import { getExcludedSearchFilters, getSearchFilters } from '../../../../contexts/queryString/selectors';
import Button from '../../../atoms/buttons/Button';
import TextField from '../../../atoms/forms/TextField';
import { IconArrowLeft, IconFilter } from '../../../atoms/images/Icon';
import FacetMenuTag from './FacetMenuTag';

interface IQueryStringContextProps {
  excludedSearchFilters: SearchFilter[];
  searchFilters: SearchFilter[];
}

interface IOwnProps {
  className?: string;
  taxonomyName: string;
  termCardinalities: ITermCardinalities | null;
  terms: GQL.ITaxonomyTerm[];
  shouldCloseFacetMenu(): Promise<{}>;
}

interface IMenuLevel {
  depth: number;
  termId?: string;
}

interface IOwnState {
  animation: {
    enter: string;
    leave: string;
  };
  filteredTerms: GQL.ITaxonomyTerm[];
  level: IMenuLevel;
}

type FacetMenuTermsProps = IOwnProps & IQueryStringContextProps;

const animationDown = {
  enter: 'transition.slideRightIn',
  leave: 'transition.slideLeftOut',
};
const animationUp = {
  enter: 'transition.slideLeftIn',
  leave: 'transition.slideRightOut',
};

const classes = new BEM('facet-menu');

class FacetMenuTerms extends React.Component<FacetMenuTermsProps, IOwnState> {
  private tags: JSX.Element[] = [];
  private levelTerms: GQL.ITaxonomyTerm[] = [];
  private subTerms: { [parentId: string]: GQL.ITaxonomyTerm[] } = {};

  constructor(props: FacetMenuTermsProps) {
    super(props);

    this.levelTerms = props.terms;
    this.state = {
      animation: animationDown,
      filteredTerms: props.terms,
      level: {
        depth: 0,
      },
    };

    this.setSubTerms();
    this.captureTags();
  }

  componentWillReceiveProps(nextProps: FacetMenuTermsProps) {
    if (this.shouldRefreshTagsList(nextProps)) {
      this.setState({ filteredTerms: nextProps.terms });
    }
  }

  componentWillUpdate(nextProps: FacetMenuTermsProps, nextState: IOwnState) {
    if (!isEqual(nextState.filteredTerms, this.state.filteredTerms)
     || !nextProps.termCardinalities) {
      this.setSubTerms(nextProps.terms);
      this.captureTags(true, nextProps, nextState);
    }
  }

  componentWillUnmount() {
    this.tags = [];
  }

  onFilterTerms = (terms: GQL.ITaxonomyTerm[], value) => {
    const filteredTerms = terms.filter(({ name }) =>
      fuzzyFilter(sanitizeString(value), sanitizeString(name!)),
    );

    this.setState({ filteredTerms });
  }

  onClickSubTags = (termId: string) => {
    this.levelTerms = this.subTerms[termId].filter((childTerm) => {
      return this.props.termCardinalities ? !!this.props.termCardinalities[childTerm.internal_id!] : true;
    });

    const nextLevel = {
      depth: this.state.level.depth + 1,
      termId,
    };

    // this.tags = this.levelTerms.map(this.getTag);

    this.setState({
      animation: animationDown,
      filteredTerms: this.levelTerms,
      level: nextLevel,
    });
  }

  onClickBackButton = () => {
    const previousLevel = {
      depth: this.state.level.depth - 1,
      termId: undefined,
    };
    this.levelTerms = this.props.terms;
    this.captureTags(true, this.props, this.state);
    this.setState({
      animation: animationUp,
      filteredTerms: this.props.terms,
      level: previousLevel,
    });
  }

  getTag = (term: GQL.ITaxonomyTerm, termCardinalities: ITermCardinalities | null, level: IMenuLevel) => {
    return (
      <FacetMenuTag
        cardinality={termCardinalities ? termCardinalities[term.internal_id!] : undefined}
        hasSubtags={this.showSubtagsButton(term.id, level.termId || '')}
        key={`${term.id}-${level.depth}`}
        onClickSubTags={this.onClickSubTags}
        term={term}
        onClose={this.props.shouldCloseFacetMenu}
      />
    );
  }

  renderTags() {
    if (this.tags.length) {
      return this.tags;
    }

    this.captureTags();
    return this.tags.length ? this.tags : null;
  }

  render() {
    const { className } = this.props;

    const hasFilterInput = this.levelTerms.length > 15;

    const extraClasses = classNames(className);

    return (
      <div {...classes('', 'terms', extraClasses) }>
        {(this.state.level.depth > 0 || hasFilterInput) && (
          <div {...classes('header') }>
            {this.state.level.depth > 0 && (
              <Button
                onClick={this.onClickBackButton}
                variant="icon"
                icon={<IconArrowLeft />}
                size="small-medium"
                colorScheme="light"
                {...classes('back-button') }
              />
            )}
            {hasFilterInput && (
              <TextField
                colorScheme="white"
                name="filter-facet-terms"
                inlineButton={<Button variant="icon" icon={<IconFilter />} disabled />}
                placeholder="Find a term"
                size="small"
                onChange={(e, value) => this.onFilterTerms(this.levelTerms, value)}
                {...classes('input-filter') }
              />
            )}
          </div>
        )}
        <div {...classes('term-list__wrapper') }>
          <VelocityTransitionGroup
            component="ul"
            enter={{ animation: this.state.animation.enter, delay: 350, display: 'flex', duration: 250 }}
            leave={{ animation: this.state.animation.leave, duration: 250 }}
            {...classes('term-list') }
          >
            {this.renderTags()}
          </VelocityTransitionGroup>
        </div>
      </div>
    );
  }

  private setSubTerms(terms = this.props.terms) {
    this.subTerms = terms.reduce((acc, term) => {
      if (term.children!.edges!.length) {
        acc[term.id] = term.children!.edges!.map(({ node }) => node!);
      }

      return acc;
    }, {});
  }

  private captureTags(reset = false, props = this.props, state = this.state) {
    const {
      excludedSearchFilters,
      searchFilters,
      taxonomyName,
      termCardinalities,
    } = props;

    if (reset) {
      this.tags = [];
    }

    let filteredTerms: GQL.ITaxonomyTerm[];
    const notSearchedTerms = state.filteredTerms.filter((term) =>
      !searchFilters.some((filter) => filter.name === term.name!)
      && !excludedSearchFilters.some((filter) => filter.name === term.name!));

    switch (taxonomyName) {
      case 'Genre':
        const firstLevelTerms = notSearchedTerms.filter((term) =>
          state.level.depth > 0 || (state.level.depth === 0 && term.children && term.children!.edges!.length));
        filteredTerms = firstLevelTerms.length ? firstLevelTerms : notSearchedTerms;
        break;
      case 'Mood':
        filteredTerms = termCardinalities ?
          notSearchedTerms.filter((term) => termCardinalities[term.internal_id!] > 1)
          : notSearchedTerms;
        break;
      default:
        filteredTerms = notSearchedTerms;
        break;
    }

    const relevantTerms = termCardinalities ?
      filteredTerms.sort((a, b) => termCardinalities[a.internal_id!] > termCardinalities[b.internal_id!] ? -1 : 1)
      : filteredTerms;

    this.tags = relevantTerms.map((term) => this.getTag(term, termCardinalities, state.level));
  }

  private shouldRefreshTagsList(nextProps: FacetMenuTermsProps) {
    const {
      termCardinalities,
      terms,
    } = nextProps;

    // if (!isEqual(excludedSearchFilters, this.props.excludedSearchFilters)) {
    //   return true;
    // }
    // if (!isEqual(searchFilters, this.props.searchFilters)) {
    //   return true;
    // }

    if (!isEqual(terms, this.props.terms)) {
      return true;
    }
    if (!isEqual(termCardinalities, this.props.termCardinalities)) {
      return true;
    }

    return false;
  }

  private showSubtagsButton(termId: string, levelTermId: string) {
    return this.props.taxonomyName === 'Genre' && this.subTerms.hasOwnProperty(termId) && levelTermId !== termId;
  }
}

const FacetMenuTermsWithQueryStringContext: React.SFC<IOwnProps> = (props) => (
  <QueryStringContext.Consumer>
    {(ctx) => (
      <FacetMenuTerms
        excludedSearchFilters={getExcludedSearchFilters(ctx)}
        searchFilters={getSearchFilters(ctx)}
        {...props}
      />
    )}
  </QueryStringContext.Consumer>
);

export default FacetMenuTermsWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/search/FacetMenu/FacetMenuTerms.tsx